import { useState, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import translations from './translations';

const STORAGE_KEY = 'preferred_language';

const SUPPORTED_LANGS = ['pt', 'en', 'es', 'fr', 'de', 'it'];

export function detectLang() {
  try {
    // ?lang= param takes priority so the language switcher (which navigates to
    // ?lang=) overrides any previously saved preference. The canonical page (no
    // ?lang=) still falls through to localStorage / default 'pt'.
    const urlLang = new URLSearchParams(window.location.search).get('lang');
    if (SUPPORTED_LANGS.includes(urlLang)) return urlLang;
    const saved = localStorage.getItem(STORAGE_KEY);
    if (SUPPORTED_LANGS.includes(saved)) return saved;
    // Portuguese business, Portuguese primary market — default to Portuguese so the
    // canonical page (what crawlers index) renders Portuguese content.
    return 'pt';
  } catch {
    return 'pt';
  }
}

export function useLanguage() {
  const [lang, setLangState] = useState(() => detectLang());
  const location = useLocation();

  // Re-detect on client-side route/query changes so the SeoLangSwitcher
  // (which navigates to ?lang=) updates the UI without a full page reload.
  useEffect(() => {
    setLangState(detectLang());
  }, [location.pathname, location.search]);

  useEffect(() => {
    const handler = () => setLangState(detectLang());
    window.addEventListener('languageChanged', handler);
    return () => window.removeEventListener('languageChanged', handler);
  }, []);

  const setLanguage = useCallback((newLang) => {
    const lang = SUPPORTED_LANGS.includes(newLang) ? newLang : 'pt';
    localStorage.setItem(STORAGE_KEY, lang);
    setLangState(lang);
    window.dispatchEvent(new Event('languageChanged'));
  }, []);

  const t = useCallback((key) => {
    return translations[lang]?.[key] || translations.pt?.[key] || key;
  }, [lang]);

  return { lang, setLanguage, t };
}